/**
 * Custom Product Thumbnail Carousel
 * For Featured Product Section
 */
class ProductThumbnailCarousel {
  constructor() {
    this.wrapper = document.querySelector('.product-thumbnail-carousel-wrapper');
    if (!this.wrapper) return;
    
    this.track = this.wrapper.querySelector('.product-thumbnail-carousel-track');
    this.thumbnails = this.track.querySelectorAll('.product-thumbnail-carousel-item');
    this.prevBtn = this.wrapper.querySelector('.thumbnail-carousel-nav--prev');
    this.nextBtn = this.wrapper.querySelector('.thumbnail-carousel-nav--next');
    this.gallery = document.querySelector('product-gallery');
    
    this.currentIndex = 0;
    this.visibleCount = this.getVisibleCount();
    
    this.init();
  }
  
  init() {
    this.attachEvents();
    this.updateNavButtons();
    window.addEventListener('resize', () => {
      this.visibleCount = this.getVisibleCount();
      this.updateNavButtons();
    });
  }
  
  getVisibleCount() {
    const container = this.wrapper.querySelector('.product-thumbnail-carousel');
    const containerWidth = container.offsetWidth;
    const isMobile = window.innerWidth < 750;
    const isTablet = window.innerWidth < 990;
    
    let thumbnailWidth;
    if (isMobile) {
      thumbnailWidth = 70 + 8; // width + gap
    } else if (isTablet) {
      thumbnailWidth = 100 + 12; // width + gap
    } else {
      thumbnailWidth = 120 + 16; // width + gap
    }
    
    return Math.floor(containerWidth / thumbnailWidth);
  }
  
  attachEvents() {
    // Thumbnail clicks
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        this.selectThumbnail(index);
        this.changeMainImage(thumbnail);
      });
    });
    
    // Navigation buttons
    this.prevBtn?.addEventListener('click', () => this.scroll('prev'));
    this.nextBtn?.addEventListener('click', () => this.scroll('next'));
    
    // Listen to main slider changes
    if (this.gallery) {
      const sliderButtons = this.gallery.querySelectorAll('.slider-button');
      sliderButtons.forEach(button => {
        button.addEventListener('click', () => {
          setTimeout(() => this.syncWithMainSlider(), 150);
        });
      });
      
      // Listen to slider drag/swipe
      const slider = this.gallery.querySelector('.slider');
      if (slider) {
        slider.addEventListener('scroll', () => {
          clearTimeout(this.scrollTimeout);
          this.scrollTimeout = setTimeout(() => this.syncWithMainSlider(), 150);
        });
      }
    }
  }
  
  changeMainImage(thumbnail) {
    const mediaId = thumbnail.dataset.mediaId;
    
    if (!this.gallery) {
      console.warn('Product gallery not found');
      return;
    }
    
    // Find the corresponding slide
    const allSlides = this.gallery.querySelectorAll('.slider__slide');
    const targetSlide = Array.from(allSlides).find(
      slide => slide.dataset.mediaId === mediaId
    );
    
    if (!targetSlide) {
      console.warn('Target slide not found for media ID:', mediaId);
      return;
    }
    
    const slideIndex = Array.from(allSlides).indexOf(targetSlide);
    
    // Try multiple methods to change the slide
    if (typeof this.gallery.setActiveMedia === 'function') {
      this.gallery.setActiveMedia(slideIndex);
    } else {
      // Fallback: manually trigger slider
      const slider = this.gallery.querySelector('.slider');
      if (slider) {
        const slideWidth = allSlides[0].offsetWidth;
        slider.scrollTo({
          left: slideIndex * slideWidth,
          behavior: 'smooth'
        });
      }
      
      // Update counter
      const counter = this.gallery.querySelector('.slider-counter--current');
      if (counter) {
        counter.textContent = slideIndex + 1;
      }
      
      // Update aria-hidden
      allSlides.forEach((slide, idx) => {
        if (idx === slideIndex) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });
    }
  }
  
  selectThumbnail(index) {
    this.thumbnails.forEach(t => t.classList.remove('active'));
    this.thumbnails[index]?.classList.add('active');
  }
  
  scroll(direction) {
    const isMobile = window.innerWidth < 750;
    const isTablet = window.innerWidth < 990;
    
    let thumbnailWidth;
    if (isMobile) {
      thumbnailWidth = 70 + 8;
    } else if (isTablet) {
      thumbnailWidth = 100 + 12;
    } else {
      thumbnailWidth = 120 + 16;
    }
    
    if (direction === 'prev') {
      this.currentIndex = Math.max(0, this.currentIndex - 1);
    } else {
      this.currentIndex = Math.min(
        this.thumbnails.length - this.visibleCount,
        this.currentIndex + 1
      );
    }
    
    const offset = this.currentIndex * thumbnailWidth;
    this.track.style.transform = `translateX(-${offset}px)`;
    this.updateNavButtons();
  }
  
  updateNavButtons() {
    if (!this.prevBtn || !this.nextBtn) return;
    
    this.prevBtn.disabled = this.currentIndex === 0;
    this.nextBtn.disabled = this.currentIndex >= this.thumbnails.length - this.visibleCount;
    
    // Hide buttons if all thumbnails are visible
    if (this.thumbnails.length <= this.visibleCount) {
      this.prevBtn.style.display = 'none';
      this.nextBtn.style.display = 'none';
    } else {
      this.prevBtn.style.display = 'flex';
      this.nextBtn.style.display = 'flex';
    }
  }
  
  syncWithMainSlider() {
    if (!this.gallery) return;
    
    const allSlides = this.gallery.querySelectorAll('.slider__slide');
    const activeSlide = Array.from(allSlides).find(
      slide => !slide.hasAttribute('aria-hidden') || slide.getAttribute('aria-hidden') === 'false'
    );
    
    if (!activeSlide) return;
    
    const mediaId = activeSlide.dataset.mediaId;
    const matchingThumbnail = Array.from(this.thumbnails).findIndex(
      t => t.dataset.mediaId === mediaId
    );
    
    if (matchingThumbnail !== -1) {
      this.selectThumbnail(matchingThumbnail);
      
      // Auto-scroll to show active thumbnail
      if (matchingThumbnail < this.currentIndex) {
        this.currentIndex = matchingThumbnail;
        this.scroll('update');
      } else if (matchingThumbnail >= this.currentIndex + this.visibleCount) {
        this.currentIndex = matchingThumbnail - this.visibleCount + 1;
        this.scroll('update');
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ProductThumbnailCarousel());
} else {
  new ProductThumbnailCarousel();
}